import { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { T, gibtText, useT } from "../i18n";
import { Fehler } from "../daten/klient";
import { betragFeldtext, zahlLesen } from "../daten/zahlfeld";
import { doppelterDruck } from "../daten/doppelt";
import { useSitzung } from "../schale/Sitzung";

/* ══════════════════════════════════════════════════════════════════
   Die sechs Bausteine aus 28-designkonzept.md, als Komponenten.

   Die Zahl bleibt: Was sich damit nicht bauen lässt, ist wahrscheinlich
   falsch gedacht. Die Stile stehen in @mrmachine/ui/basis.css — hier
   steht nur, welche Klasse wann gilt, und die Zugänglichkeit.

   **Klassennamen werden nicht erfunden.** Jeder hier benutzte Name
   steht schon in basis.css oder im Prototyp. Wer einen neuen braucht,
   sucht vorher, ob es ihn gibt (der Fall `.anfang` steht in basis.css
   im Kopfkommentar).
   ══════════════════════════════════════════════════════════════════ */

type KnopfArt = "" | "haupt" | "stumm";

export function Knopf({
  art = "", klein, kinder, children, einmal, onClick, ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  art?: KnopfArt; klein?: boolean; kinder?: ReactNode;
  /**
   * Binnen einer Sekunde nur ein Druck (`daten/doppelt.ts`, Playbook K-01,
   * S-01, K-14). Vorgabe beim Hauptknopf — er legt an, schickt ab, nimmt an.
   * `einmal={false}` für einen Hauptknopf, der bewusst schnell hintereinander
   * gedrückt wird; `einmal` für jeden anderen, der etwas anlegt.
   */
  einmal?: boolean;
}) {
  const klassen = ["knopf", art, klein ? "klein" : ""].filter(Boolean).join(" ");
  const letzter = useRef<number | null>(null);
  const riegel = einmal ?? art === "haupt";
  return (
    <button
      type="button"
      {...rest}
      onClick={(e) => {
        if (riegel) {
          const jetzt = Date.now();
          /* Der zweite Druck eines Doppelklicks — auch der, der ein
             Formular abschicken würde (`type="submit"`): `preventDefault`
             hält das Absenden an. */
          if (doppelterDruck(letzter.current, jetzt)) { e.preventDefault(); return; }
          letzter.current = jetzt;
        }
        onClick?.(e);
      }}
      className={`${klassen}${rest.className ? ` ${rest.className}` : ""}`}
    >
      {children ?? kinder}
    </button>
  );
}

/** Zustand, nie Aktion. Nicht anklickbar — das ist der Unterschied zum Knopf. */
export function Marke({ art = "", punkt, zustand, children }: {
  art?: "" | "gut" | "acht" | "stopp"; punkt?: boolean;
  /** Ein Zustand der Akte („Entwurf", „deaktiviert") — keine Zahl. Bekommt
      Form + Wort wie die farbigen Zustände (neutral: leerer Ring) und wird
      nicht zum leisen Zählersatz neben dem Titel (Paket 8 Signal). */
  zustand?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`marke${art ? ` ${art}` : ""}${zustand ? " zustand" : ""}`}>
      {punkt && art ? <span className={`pkt ${art}`} /> : null}
      {zustand && !art ? <span className="pkt ring" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

/**
 * Aktion ist Fläche, Zustand ist Punkt.
 *
 * Die mittlere Ampel ist ein **offener Ring**, nie gefüllt — so ist sie
 * von einem Knopf nicht zu verwechseln, und sie trägt auch für jemanden,
 * der Rot und Grün nicht unterscheidet.
 */
export function Punkt({ art }: { art: "gut" | "acht" | "stopp" }) {
  return <span className={`pkt ${art}`} />;
}

/**
 * Leer: Ein Satz, der sagt was fehlt, und der Knopf, der es anlegt.
 * Nie eine leere Tabelle mit Kopfzeile.
 */
export function Leer({ satz, knopf, children }: {
  satz: string; knopf?: ReactNode;
  /** Warum es leer ist und was das bedeutet — als `<p className="leer-satz">`. */
  children?: ReactNode;
}) {
  return (
    <div className="leer">
      <p><T k={satz} /></p>
      {children}
      {knopf}
    </div>
  );
}

/**
 * Lädt: Die Zeilenform bleibt stehen, grau gefüllt — kein Kreisel, der
 * die Seite springen lässt.
 */
export function Laedt({ zeilen = 6 }: { zeilen?: number }) {
  const t = useT();
  return (
    <div className="lade-raum" role="status" aria-label={t("zustand.laedt")}>
      {Array.from({ length: zeilen }, (_, i) => <div className="lade-zeile" key={i} />)}
    </div>
  );
}

/**
 * Die Meldung sagt, was war **und** was zu tun ist. Eine
 * Constraint-Verletzung durchzureichen ist faul — der Anwender weiß
 * selbst, dass es nicht ging.
 */
export function Meldung({ art = "stopp", titel, children, knoepfe }: {
  art?: "stopp" | "acht"; titel: string; children?: ReactNode; knoepfe?: ReactNode;
}) {
  return (
    <div className={`meldung${art === "acht" ? " acht" : ""}`}>
      <span className={`pkt ${art}`} style={{ marginTop: 5 }} />
      <div className="meldung-text">
        <b><T k={titel} /></b>
        {children}
        {knoepfe ? <div className="meldung-knoepfe">{knoepfe}</div> : null}
      </div>
    </div>
  );
}

/**
 * Ein Fehler aus der API — mit dem Weg heraus, nicht nur mit dem Grund.
 *
 * **`fehler` gibt den Fehler selbst mit** (03.10.2026, A-04/K-15/D-25). Dann
 * kommen die Werte für den Satz aus ihm (`Fehler.werte`), ohne dass die Maske
 * sie heraussuchen muss; `pfad` darf dann fehlen. Ein eigenes `werte` geht
 * vor — für Masken, die einen Wert anders zeigen wollen.
 */
export function FehlerBand({ pfad: eigenerPfad, werte: eigeneWerte, fehler, nochmal }: {
  pfad?: string; werte?: Record<string, string | number> | undefined; fehler?: unknown; nochmal?: () => void;
}) {
  const t = useT();
  const pfad = eigenerPfad ?? (fehler instanceof Fehler ? fehler.textPfad : "fehler.unbekannt");
  const roh = fehler instanceof Fehler || eigeneWerte
    ? { ...(fehler instanceof Fehler ? fehler.werte : {}), ...(eigeneWerte ?? {}) }
    : undefined;
  /* **Feldnamen statt Spaltennamen** (K-15): `kunde.pflichtfelder` nennt die
     Felder so, wie die Einstellung sie speichert — `ansprechpartner`. Im Satz
     steht das Wort der Maske. */
  const werte = roh && typeof roh["felder"] === "string"
    ? {
        ...roh,
        felder: roh["felder"].split(/\s*,\s*/).filter(Boolean)
          .map((f) => (gibtText(`fehler.feldwort.${f}`) ? t(`fehler.feldwort.${f}`) : f)).join(", "),
      }
    : roh;
  /* **Was Lexware Office beanstandet hat** (Systemprüfung B-07). Eine
     Ablehnung von drüben bringt das Feld und den Wortlaut mit
     (`feld`, `lexware_antwort`); ohne sie stand „abgelehnt" da und sonst
     nichts. Das Feld auf Deutsch, wo es einen Namen dafür gibt — der
     Wortlaut darunter so, wie er kam: Er ist das, was man dem Support
     vorliest. Nur bei Lexware-Meldungen: `feld` heißt anderswo etwas anderes. */
  const vonLexware = pfad.startsWith("fehler.lexware");
  const feld = vonLexware && typeof werte?.["feld"] === "string" ? werte["feld"] : "";
  const feldPfad = feld ? `fehler.lexwareFeld.${feld.replace(/\[\d+\]/g, "").replace(/[^A-Za-z0-9]+/g, "_")}` : "";
  const feldWort = feldPfad && gibtText(feldPfad) ? t(feldPfad) : "";
  const antwort = vonLexware && typeof werte?.["lexware_antwort"] === "string" ? werte["lexware_antwort"] : "";
  return (
    <div className="meldung">
      <span className="pkt stopp" style={{ marginTop: 5 }} />
      <div className="meldung-text">
        <b><T k={pfad} {...(werte ? { werte } : {})} /></b>
        {feldWort ? <p className="nebensatz">{t("fehler.lexwareBeanstandet", { feld: feldWort })}</p> : null}
        {antwort ? <p className="nebensatz">{t("fehler.lexwareWortlaut", { antwort })}</p> : null}
        {nochmal ? (
          <div className="meldung-knoepfe">
            <Knopf klein onClick={nochmal}>{t("fehler.erneutVersuchen")}</Knopf>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Die Schlüssel-Wert-Liste: links grau, rechts in Mono auf einer Kante. */
export function Kv({ children }: { children: ReactNode }) {
  return <dl className="kv">{children}</dl>;
}

/** Erklärender Fließtext gehört nicht in eine rechtsbündige Mono-Spalte. */
export function Merkmale({ children }: { children: ReactNode }) {
  return <dl className="merkmale">{children}</dl>;
}

/**
 * Das Fragezeichen — die andere Hälfte der Regel aus 28-designkonzept.md.
 *
 * Dort steht: *Ein Satz, der bei jedem Aufruf dasselbe erklärt, gehört nicht
 * auf den Schirm.* Nur gilt das für den **Schirm**, nicht für die Erklärung.
 * Manche Begriffe muss man einmal erklären — „Kundentext" ist so einer, und
 * wer ihn nicht kennt, schreibt sonst eine interne Notiz auf die Rechnung.
 *
 * Also steht der Satz nicht da, sondern liegt hinter einem Zeichen: sichtbar
 * für den, der fragt, weg für alle anderen. Das Zeichen ist ein Ring, kein
 * gefüllter Knopf — es löst nichts aus, es zeigt nur.
 *
 * Aufgeklappt wird beim Zeigen und beim Klicken. Der Klick hält fest, das
 * Zeigen nicht: Wer mit der Maus vorbeifährt, will nichts festhalten, und wer
 * mit der Tastatur kommt, hat keine Maus, an der etwas hängen bleibt.
 */
/**
 * **Ohne Fragezeichen** (Luca, 08.10.2026, Vermieten am Schreibtisch): *„Die
 * Fragezeichen-Hinweise fallen weg. Was wichtig ist, steht als `title` am
 * Element."* Eine Maske, die das will, legt diesen Rahmen um ihren Inhalt;
 * jede `Erklaerung` darin zeichnet dann kein Zeichen, sondern hängt ihren Satz
 * als `title` an das Element, in dem sie steht (wenn es noch keinen hat). So
 * gilt es auch für Bausteine, die die Maske nicht selbst schreibt.
 */
const ErklaerungAlsTitelRahmen = createContext(false);
export function ErklaerungAlsTitel({ children }: { children: ReactNode }) {
  return <ErklaerungAlsTitelRahmen.Provider value>{children}</ErklaerungAlsTitelRahmen.Provider>;
}

export function Erklaerung(props: {
  /** i18n-Pfad des einen Satzes. Zwei Sätze sind schon zu viel. */
  k: string;
  werte?: Record<string, string | number> | undefined;
}) {
  return useContext(ErklaerungAlsTitelRahmen) ? <ErklaerungTitel {...props} /> : <ErklaerungZeichen {...props} />;
}

/** Der Satz als `title` am umgebenden Element — kein Zeichen, kein Platz. */
function ErklaerungTitel({ k, werte }: { k: string; werte?: Record<string, string | number> | undefined }) {
  const t = useT();
  const anker = useRef<HTMLSpanElement>(null);
  const satz = t(k, werte);
  useLayoutEffect(() => {
    const el = anker.current?.parentElement;
    if (!el || el.title) return undefined;
    el.title = satz;
    return () => { if (el.title === satz) el.removeAttribute("title"); };
  }, [satz]);
  return <span ref={anker} hidden />;
}

function ErklaerungZeichen({ k, werte }: {
  k: string;
  werte?: Record<string, string | number> | undefined;
}) {
  const t = useT();
  const id = useId();
  const [fest, setzeFest] = useState(false);
  const [schwebt, setzeSchwebt] = useState(false);
  const raum = useRef<HTMLSpanElement>(null);
  const satz = useRef<HTMLSpanElement>(null);
  const offen = fest || schwebt;

  /* Der Satz hängt am Zeichen, und das Zeichen steht mitten in einer Zeile --
     in der 296 px schmalen Leiste läuft er damit über den Rand, sobald die
     Beschriftung ein Wort länger ist. Statt eine Breite zu raten, die überall
     passt, wird er nach dem Aufklappen einmal zurückgeschoben, bis er im
     Kasten steht, der ihn sonst abschneiden würde. */
  useLayoutEffect(() => {
    const el = satz.current;
    if (!offen || !el) return;
    el.style.marginLeft = "0px";

    let kasten: HTMLElement | null = raum.current?.parentElement ?? null;
    while (kasten && getComputedStyle(kasten).overflowX === "visible") {
      kasten = kasten.parentElement;
    }
    const grenze = kasten
      ? kasten.getBoundingClientRect()
      : new DOMRect(0, 0, document.documentElement.clientWidth, window.innerHeight);

    const luft = 8;
    const r = el.getBoundingClientRect();
    let schub = 0;
    if (r.right > grenze.right - luft) schub = grenze.right - luft - r.right;
    if (r.left + schub < grenze.left + luft) schub = grenze.left + luft - r.left;
    if (schub !== 0) el.style.marginLeft = `${Math.round(schub)}px`;
  }, [offen]);

  /* Ein Klick irgendwo anders schließt — sonst bleibt der Satz stehen, wenn
     man weiterarbeitet, und ist genau das, was er nicht sein soll. */
  useEffect(() => {
    if (!fest) return undefined;
    function anderswo(e: MouseEvent) {
      if (!raum.current?.contains(e.target as Node)) setzeFest(false);
    }
    function taste(e: KeyboardEvent) {
      if (e.key === "Escape") setzeFest(false);
    }
    document.addEventListener("mousedown", anderswo);
    document.addEventListener("keydown", taste);
    return () => {
      document.removeEventListener("mousedown", anderswo);
      document.removeEventListener("keydown", taste);
    };
  }, [fest]);

  return (
    <span
      className="erklaerung"
      ref={raum}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") setzeSchwebt(true); }}
      onPointerLeave={() => setzeSchwebt(false)}
    >
      <button
        type="button"
        className={`erklaerung-zeichen${offen ? " offen" : ""}`}
        aria-label={t("erklaerung.was")}
        aria-expanded={offen}
        {...(offen ? { "aria-describedby": id } : {})}
        onClick={() => setzeFest((w) => !w)}
        onFocus={() => setzeSchwebt(true)}
        onBlur={() => setzeSchwebt(false)}
      >
        ?
      </button>
      {offen ? (
        <span className="erklaerung-satz" id={id} role="note" ref={satz}>
          <T k={k} {...(werte ? { werte } : {})} />
        </span>
      ) : null}
    </span>
  );
}

/* ══════════════ Die drei Zustände, die überall gleich aussehen ══════════════
   Jede Maske braucht fünf: Leer, Lädt, Fehler, Kein Recht, Offline. Die
   ersten beiden baut jede Maske selbst — sie sehen je nach Inhalt anders
   aus. Die letzten drei sehen überall gleich aus, und darum stehen sie
   hier an einer Stelle statt in jeder Maske noch einmal. */
export function Zustandsband({ fehler, nochmal }: { fehler: unknown; nochmal: () => void }) {
  const t = useT();
  const { ich } = useSitzung();

  // Offline: kein Serverfehler, sondern gar keine Antwort. Der Weg heraus
  // ist derselbe Knopf — sobald das Netz wieder da ist, trägt er.
  if (fehler instanceof Fehler && fehler.schluessel === "netz") {
    return <FehlerBand pfad="fehler.netz" nochmal={nochmal} />;
  }

  // Kein Recht: Die Richtlinie liefert leer, die Maske meckert nicht.
  // Ein „Erneut versuchen" wäre hier gelogen — es ändert sich nichts.
  if (fehler instanceof Fehler && fehler.status === 403) {
    const erste = ich?.rollen[0];
    const rolle = erste
      ? t(`benutzer.rolle${erste.charAt(0).toUpperCase()}${erste.slice(1)}`)
      : t("zustand.keinZugangMarke");
    return (
      <div className="meldung">
        <span className="pkt stopp" style={{ marginTop: 5 }} />
        <div className="meldung-text">
          <b>{t("zustand.keinZugangMarke")}</b>
          <T k="zustand.keinZugang" werte={{ rolle }} />
        </div>
      </div>
    );
  }

  /* **Mit den Werten, die der Fehler mitbringt** (Systemprüfung I-03,
     24.09.2026). Ohne sie stand in einem Satz wie „{qual} von {person} ist am
     {gueltig_bis} abgelaufen" genau das — die Platzhalter statt der Namen.
     Seit dem 03.10.2026 liest `FehlerBand` sie selbst (`Fehler.werte`). */
  return <FehlerBand fehler={fehler} nochmal={nochmal} />;
}

/**
 * Ein Betrag, wie ihn ein Mensch liest.
 *
 * Steht an drei Stellen gebraucht -- Abrechnung, Preise, Schnellerfassung --
 * und stand deshalb dreimal da. Hier einmal.
 *
 * `de-DE` ist hier kein sichtbarer Text, sondern eine Zahlenordnung: Punkt
 * als Tausender, Komma als Komma. Die Waehrung steht daneben, weil sie in
 * einer Spalte mit Zahlen sonst die Ausrichtung bricht.
 */
export function euro(betrag: number | null | undefined): string {
  if (betrag === null || betrag === undefined || !Number.isFinite(betrag)) return "—";
  return betrag.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Eine Zahl, wie sie in ein **Betragsfeld** geschrieben wird: zwei Stellen,
 * Komma, kein Tausenderpunkt.
 *
 * Nicht dasselbe wie `euro`: Das ist zum Lesen, das hier zum Bearbeiten. Ein
 * Feld mit „1.250,00" darin gibt beim nächsten Tastendruck eine andere Zahl
 * her, als daneben steht.
 *
 * Seit N6-01 (04.10.2026) nur noch ein anderer Name für `betragFeldtext`
 * (`daten/zahlfeld.ts`) — es gibt eine Schreibweise, nicht drei.
 */
export const betragstext = (x: number): string => betragFeldtext(x);

/**
 * `48,50` → `48.5`. Leer heißt `undefined`: nichts getippt, also gilt, was
 * gerechnet ist — und nicht null.
 */
export function alsZahl(text: string): number | undefined {
  // Die eine Lesung (daten/zahlfeld.ts): `48.50` ist 48,50 und nicht 4 850.
  return zahlLesen(text) ?? undefined;
}
